import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import SkalaLogo from "../components/SkalaLogo";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { trackGa4Login } from "../lib/ga4";

export default function LoginPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { login } = useAuth();
    const { language } = useLanguage();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const copy = {
        en: {
            required: "Email and password are required.",
            welcome: "Welcome back",
            fail: "Login failed.",
            back: "Back to site",
            heroTitle1: "Welcome back to your",
            heroTitle2: "growth desk",
            heroSubtitle: "Sign in to manage your wallet, deposits and agency ad accounts.",
            heroFoot: "Adrise · Crypto-funded agency ad accounts",
            title: "Sign in",
            firstTime: "First time here?",
            apply: "Create a free account",
            email: "Email",
            password: "Password",
            emailPh: "team@brand.com",
            submit: "Sign in",
            submitting: "Signing in…",
            legal: "By signing in, you agree to our Privacy Policy and Service Agreement.",
        },
        pt: {
            required: "E-mail e senha são obrigatórios.",
            welcome: "Bem-vindo de volta",
            fail: "Falha no login.",
            back: "Voltar ao site",
            heroTitle1: "Bem-vindo de volta ao seu",
            heroTitle2: "painel de crescimento",
            heroSubtitle: "Entre para gerenciar sua wallet, depósitos e contas de anúncios.",
            heroFoot: "Adrise · Contas de agência com recarga em cripto",
            title: "Entrar",
            firstTime: "Primeira vez aqui?",
            apply: "Criar conta grátis",
            email: "E-mail",
            password: "Senha",
            emailPh: "time@marca.com",
            submit: "Entrar",
            submitting: "Entrando…",
            legal: "Ao entrar, você concorda com nossa Política de Privacidade e Termos de Serviço.",
        },
        es: {
            required: "Correo y contraseña son obligatorios.",
            welcome: "Bienvenido de nuevo",
            fail: "Error de inicio de sesión.",
            back: "Volver al sitio",
            heroTitle1: "Bienvenido de nuevo a tu",
            heroTitle2: "panel de crecimiento",
            heroSubtitle: "Inicia sesión para gestionar tu wallet, depósitos y cuentas publicitarias.",
            heroFoot: "Adrise · Cuentas de agencia fondeadas con cripto",
            title: "Iniciar sesión",
            firstTime: "¿Primera vez aquí?",
            apply: "Crear cuenta gratis",
            email: "Correo",
            password: "Contraseña",
            emailPh: "equipo@marca.com",
            submit: "Iniciar sesión",
            submitting: "Iniciando sesión…",
            legal: "Al iniciar sesión, aceptas nuestra Política de Privacidad y Términos de Servicio.",
        },
        ru: {
            required: "E-mail и пароль обязательны.",
            welcome: "С возвращением",
            fail: "Не удалось войти.",
            back: "Вернуться на сайт",
            heroTitle1: "С возвращением в",
            heroTitle2: "панель роста",
            heroSubtitle: "Войдите, чтобы управлять кошельком, депозитами и рекламными аккаунтами.",
            heroFoot: "Adrise · Агентские рекламные аккаунты с крипто-пополнением",
            title: "Войти",
            firstTime: "Впервые здесь?",
            apply: "Создать бесплатный аккаунт",
            email: "E-mail",
            password: "Пароль",
            emailPh: "team@brand.com",
            submit: "Войти",
            submitting: "Выполняется вход…",
            legal: "Входя, вы принимаете Политику конфиденциальности и Соглашение об услугах.",
        },
        tr: {
            required: "E-posta ve şifre zorunludur.",
            welcome: "Tekrar hoş geldin",
            fail: "Giriş başarısız.",
            back: "Siteye dön",
            heroTitle1: "Ajans büyüme",
            heroTitle2: "paneline hoş geldin",
            heroSubtitle: "Cüzdanını, yatırımlarını ve ajans reklam hesaplarını yönetmek için giriş yap.",
            heroFoot: "Adrise · Kripto fonlu ajans reklam hesapları",
            title: "Giriş yap",
            firstTime: "İlk kez mi geliyorsun?",
            apply: "Ücretsiz hesap oluştur",
            email: "E-posta",
            password: "Şifre",
            emailPh: "sirket@ornek.com",
            submit: "Giriş yap",
            submitting: "Giriş yapılıyor…",
            legal: "Giriş yaparak Gizlilik Politikası ve Hizmet Sözleşmesini kabul etmiş olursun.",
        },
    };
    const c = copy[language] || copy.en;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error(c.required);
            return;
        }
        setSubmitting(true);
        const res = await login(email, password);
        setSubmitting(false);
        if (res.ok) {
            toast.success(`${c.welcome}, ${res.user.full_name.split(" ")[0]}!`);
            trackGa4Login("email_password");
            const next = params.get("next");
            if (next) {
                navigate(next);
            } else if (res.user.role === "admin") {
                navigate("/admin");
            } else {
                navigate("/panel");
            }
        } else {
            toast.error(res.error || c.fail);
        }
    };

    return (
        <div data-testid="login-page" className="min-h-screen grid lg:grid-cols-2">
            <div className="relative hidden lg:block overflow-hidden bg-card border-r border-white/5">
                <div className="absolute inset-0 grid-bg opacity-50" />
                <div className="absolute inset-0 noise" />
                <div
                    aria-hidden
                    className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-primary/[0.04] blur-[120px]"
                />
                <div className="relative h-full flex flex-col justify-between p-12">
                    <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm">
                        <ArrowLeft className="w-4 h-4" /> {c.back}
                    </Link>
                    <div className="space-y-6">
                        <SkalaLogo size="lg" />
                        <h2 className="font-display font-bold text-4xl leading-tight tracking-tight max-w-md">
                            {c.heroTitle1}
                            <span className="text-gradient-primary"> {c.heroTitle2}</span>.
                        </h2>
                        <p className="text-muted-foreground max-w-sm">
                            {c.heroSubtitle}
                        </p>
                    </div>
                    <p className="text-xs font-mono text-muted-foreground tracking-wider">
                        {c.heroFoot}
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 md:p-12">
                <div className="w-full max-w-md space-y-8">
                    <div className="lg:hidden">
                        <SkalaLogo />
                    </div>
                    <div className="space-y-2">
                        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                            {c.title}
                        </h1>
                        <p className="text-muted-foreground">
                            {c.firstTime}{" "}
                            <Link
                                to="/signup"
                                data-testid="login-go-to-signup"
                                className="text-primary hover:underline"
                            >
                                {c.apply}
                            </Link>
                        </p>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                        <div className="space-y-1.5">
                            <Label htmlFor="email">{c.email}</Label>
                            <Input
                                id="email"
                                data-testid="login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={c.emailPh}
                                className="bg-card border-white/10 h-11"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password">{c.password}</Label>
                            <Input
                                id="password"
                                data-testid="login-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="bg-card border-white/10 h-11"
                                required
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting}
                            data-testid="login-submit"
                            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11"
                        >
                            {submitting ? c.submitting : c.submit}
                        </Button>
                    </form>

                    <p className="text-center text-xs text-muted-foreground">
                        {c.legal}
                    </p>
                </div>
            </div>
        </div>
    );
}
